import * as React from 'react';
import { cn } from '@noor/lib/cn';
import { Label } from '../label';
import { useFormFieldContext } from './form-field-context';

/** Field label, auto-associated with the control. */
export function FormFieldLabel({
  className,
  ...props
}: React.ComponentProps<typeof Label>) {
  const { formItemId, error } = useFormFieldContext();
  return (
    <Label htmlFor={formItemId} data-error={!!error} className={className} {...props} />
  );
}

/** Helper/description text, wired to the control via aria-describedby. */
export function FormFieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormFieldContext();
  return (
    <p
      id={formDescriptionId}
      data-slot="form-field-description"
      className={cn(
        'text-[length:var(--form-description-font-size)] leading-[var(--form-description-line-height)] text-muted-foreground',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The one control→message gap, shared by every form in the app.
 *
 * A `FormField` root is a `grid gap-2`, so its 8px row gap sits between the
 * LABEL and the control as well as between the control and this message —
 * which read as far too much air under an errored input, and drifted per form
 * as each one patched it with its own `min-h`/margin overrides.
 *
 * The message therefore takes up NO room until there is something to say
 * (`empty:hidden` plus `min-h-0`, overriding the reserved line it used to
 * keep). A layout that needs the line reserved regardless — side-by-side fields
 * that must stay height-matched — reserves it on the FIELD instead, the way the
 * product wizard's `FIELD_GROUP` does.
 *
 * This constant carries NO margin, because it is shared by two layouts that
 * need opposite things. An in-flow message must cancel the grid's inherited 8px
 * (see {@link FORM_FIELD_MESSAGE_INFLOW}); an ABSOLUTELY positioned one is
 * placed by its own `top`/`bottom` and wants no flow spacing at all.
 */
export const FORM_FIELD_MESSAGE =
  'min-h-0 px-1 text-[length:var(--form-message-font-size)] font-[var(--form-message-font-weight)] leading-[var(--form-message-line-height)] empty:hidden';

/**
 * {@link FORM_FIELD_MESSAGE} plus the negative margin that cancels the
 * `FormField` grid's 6px row gap and restates it as 4px — the default for a
 * message rendered IN FLOW, which is every message that does not position
 * itself.
 */
export const FORM_FIELD_MESSAGE_INFLOW = `-mt-0.5 ${FORM_FIELD_MESSAGE}`;

/**
 * Validation message. Renders the RHF error message when present, otherwise
 * `children` (or nothing). Always carries the id referenced by the control's
 * aria-describedby, and sits the standard 4px under its control
 * ({@link FORM_FIELD_MESSAGE_INFLOW}).
 */
export function FormFieldMessage({
  className,
  children,
  ...props
}: React.ComponentProps<'p'>) {
  const { error, formMessageId } = useFormFieldContext();
  const body = error?.message ?? children;
  return (
    <p
      id={formMessageId}
      data-slot="form-field-message"
      className={cn(FORM_FIELD_MESSAGE_INFLOW, 'text-form-error-fg', className)}
      {...props}
    >
      {body}
    </p>
  );
}
